import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Global lock to prevent multiple instances from adding gifts simultaneously.
 */
const getGiftLock = () => window.__giftAddingLock || false;
const setGiftLock = (value) => { window.__giftAddingLock = value; };
const collectionProductIdsCache = new Map();

/**
 * @param {string | undefined} value
 * @returns {Set<number>}
 */
function parseProductIds(value) {
  return new Set(
    (value || '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter(Number.isFinite)
  );
}

/**
 * Component that automatically adds a gift product when enough items
 * from a trigger collection are in the cart.
 * Max 1 gift per cart. Gift product excluded from count via line item properties.
 */
class CartBogoCollectionComponent extends Component {
  #isUpdating = false;
  #triggerCollectionHandle = null;
  #giftProductHandle = null;
  #giftVariantId = null;
  #giftProductId = null;
  #minQty = 1;
  /** @type {Set<number> | null} */
  #triggerProductIds = null;

  connectedCallback() {
    super.connectedCallback();

    this.#triggerCollectionHandle = this.dataset.triggerCollection || null;
    this.#giftProductHandle = this.dataset.giftProductHandle || null;
    this.#giftVariantId = this.dataset.giftVariantId || null;
    this.#giftProductId = this.dataset.giftProductId ? parseInt(this.dataset.giftProductId, 10) : null;
    this.#minQty = parseInt(this.dataset.minQty, 10) || 1;
    this.hasBogoGift = this.hasAttribute('data-has-bogo-gift');

    if (!this.#triggerCollectionHandle || (!this.#giftProductHandle && !this.#giftVariantId)) return;

    const serverRenderedProductIds = parseProductIds(this.dataset.triggerProductIds);
    if (serverRenderedProductIds.size > 0) {
      this.#triggerProductIds = serverRenderedProductIds;
      collectionProductIdsCache.set(this.#triggerCollectionHandle, serverRenderedProductIds);
      this.#fetchTriggerProductIds(true);
    } else {
      this.#fetchTriggerProductIds();
    }

    // If no variant ID, resolve it from handle
    if (!this.#giftVariantId && this.#giftProductHandle) {
      this.#fetchGiftVariantId();
    }

    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Staggered checks cover initial cart render and drawer morph timing.
    setTimeout(() => this.#checkBogoCondition(), 100);
    setTimeout(() => this.#checkBogoCondition(), 700);
    setTimeout(() => this.#checkBogoCondition(), 1500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  async #fetchGiftVariantId() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}products/${this.#giftProductHandle}.js`);
      if (res.ok) {
        const product = await res.json();
        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (variant) {
          this.#giftVariantId = variant.id.toString();
          this.#checkBogoCondition();
        }
      }
    } catch { /* silent */ }
  }

  async #fetchTriggerProductIds(forceFetch = false) {
    if (!forceFetch && this.#triggerProductIds) return this.#triggerProductIds;
    if (!forceFetch && collectionProductIdsCache.has(this.#triggerCollectionHandle)) {
      this.#triggerProductIds = collectionProductIdsCache.get(this.#triggerCollectionHandle);
      return this.#triggerProductIds;
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}collections/${this.#triggerCollectionHandle}/products.json?limit=250`);
      if (res.ok) {
        const data = await res.json();
        const fetchedProductIds = new Set(data.products.map((p) => p.id));
        if (this.#triggerProductIds) {
          for (const id of fetchedProductIds) this.#triggerProductIds.add(id);
        } else {
          this.#triggerProductIds = fetchedProductIds;
        }
        collectionProductIdsCache.set(this.#triggerCollectionHandle, this.#triggerProductIds);
      }
    } catch { /* silent */ }
    return this.#triggerProductIds;
  }

  #handleCartUpdate = (event) => {
    const source = event.detail?.data?.source || event.detail?.source;
    if (source === 'cart-bogo-collection') return;
    this.#checkBogoCondition();
  };

  /**
   * Dispatches a proper CartUpdateEvent so the cart drawer / cart-items
   * component actually re-renders with the fresh section HTML, instead of
   * a generic event nothing listens to.
   * @param {object} [cartData] - The parsed JSON response from a cart/*.js call.
   */
  #refreshCartDrawer(cartData) {
    document.dispatchEvent(
      new CartUpdateEvent(cartData || {}, 'cart-bogo-collection', {
        itemCount: cartData?.item_count,
        sections: cartData?.sections || {},
        source: 'cart-bogo-collection',
      })
    );
  }

  async #checkBogoCondition() {
    if (this.#isUpdating || !this.#triggerCollectionHandle) return;

    const triggerProductIds = await this.#fetchTriggerProductIds();
    if (!triggerProductIds) return;

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();

      // Count qualifying items (exclude auto-added gifts by property)
      let triggerCount = 0;
      let giftItem = null;

      for (const item of cart.items) {
        if (item.properties?._bogo_collection_gift === 'true') {
          giftItem = item;
          continue;
        }
        if (item.properties?._bogo_quantity_gift === 'true') continue;
        if (item.properties?._auto_added_threshold === 'true') continue;
        if (item.properties?._freegifincart === 'true') continue;
        if (item.properties?._free_gift === 'true') continue;

        if (triggerProductIds.has(item.product_id)) {
          triggerCount += item.quantity;
        }
      }

      const shouldHaveGift = triggerCount >= this.#minQty;
      const hasGift = giftItem != null;

      if (shouldHaveGift && !hasGift && this.#giftVariantId) {
        await this.#addGift();
      } else if (!shouldHaveGift && hasGift) {
        await this.#removeGift(giftItem);
      }
      // If gift qty > 1 somehow, correct to 1
      else if (hasGift && giftItem.quantity > 1) {
        await this.#updateGiftQty(giftItem, 1);
      }
    } catch (error) {
      console.error('🛒 [BOGO Collection]', error);
    }
  }

  async #addGift() {
    if (this.#isUpdating || !this.#giftVariantId || getGiftLock()) return;
    setGiftLock(true);
    this.#isUpdating = true;

    try {
      const formData = new FormData();
      formData.append('id', this.#giftVariantId);
      formData.append('quantity', '1');
      formData.append('properties[_bogo_collection_gift]', 'true');
      formData.append('properties[_free_gift]', 'true');
      formData.append('properties[_trigger_collection]', this.#triggerCollectionHandle || '');
      if (this.dataset.sectionId) formData.append('sections', this.dataset.sectionId);

      const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!res.ok) {
        let message = `Add failed: ${res.status}`;
        try {
          const data = await res.json();
          message = data.description || data.message || message;
        } catch { /* response was not JSON */ }
        throw new Error(message);
      }

      const data = await res.json();

      this.hasBogoGift = true;
      this.setAttribute('data-has-bogo-gift', 'true');
      this.#refreshCartDrawer(data);
    } catch (error) {
      console.error('🛒 [BOGO Collection] Add error:', error);
    } finally {
      this.#isUpdating = false;
      setGiftLock(false);
    }
  }

  async #removeGift(giftItem) {
    if (this.#isUpdating) return;
    this.#isUpdating = true;

    try {
      const body = { id: giftItem.key, quantity: 0 };
      if (this.dataset.sectionId) body.sections = this.dataset.sectionId;

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        this.hasBogoGift = false;
        this.removeAttribute('data-has-bogo-gift');
        this.#refreshCartDrawer(data);
      }
    } catch (error) {
      console.error('🛒 [BOGO Collection] Remove error:', error);
    } finally {
      this.#isUpdating = false;
    }
  }

  async #updateGiftQty(giftItem, qty) {
    if (this.#isUpdating) return;
    this.#isUpdating = true;

    try {
      const body = { id: giftItem.key, quantity: qty };
      if (this.dataset.sectionId) body.sections = this.dataset.sectionId;

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        this.#refreshCartDrawer(data);
      }
    } catch (error) {
      console.error('🛒 [BOGO Collection] Update error:', error);
    } finally {
      this.#isUpdating = false;
    }
  }
}

if (!customElements.get('cart-bogo-collection')) {
  customElements.define('cart-bogo-collection', CartBogoCollectionComponent);
}